/**
 * Logical-replication health contracts (#1967 R7 / #1996).
 *
 * Tokyo-side sampling covers both legs: the downlink slot (derived
 * `ndt_downlink_<database>`, or the unqualified `ndt_downlink` when that
 * slot already belongs to this database — #2123) and the uplink
 * subscription (`ndt_uplink` in `pg_stat_subscription`). Each sample
 * also diffs that direction's live members against the compiled table
 * set (publication on the publisher side, subscription relations on
 * the subscriber side).
 *
 * Downlink *receipt* age is the CN subscriber's
 * `pg_stat_subscription.latest_end_time` lag, pulled over
 * `/internal/replication-subscriber-lag` — Tokyo cannot see that
 * catalog view, which is why this column used to be bytes.
 *
 * Uplink *delivery* has two further CN/Tokyo splits (#2375): per-table
 * `pg_subscription_rel.srsubstate` is only on Tokyo (a tablesync stuck
 * in `'d'` keeps the apply worker and receipt age healthy), and the
 * publisher slot's unconfirmed WAL is only on CN, pulled over
 * `/internal/replication-uplink-slot`. Neither field is a samples-table
 * column; stalled relation counts ride the cron response and the alert
 * text, and unconfirmed bytes reuse `apply_lag_bytes`.
 */

export const REPLICATION_LINK_NAMES = ['downlink', 'uplink'] as const;
export type ReplicationLinkName = (typeof REPLICATION_LINK_NAMES)[number];

export const REPLICATION_HEALTH_STATUSES = ['absent', 'ok', 'warn', 'crit', 'unknown'] as const;
export type ReplicationHealthStatus = (typeof REPLICATION_HEALTH_STATUSES)[number];
/** Statuses the sampler may persist. `unknown` is a read-time overlay. */
export const REPLICATION_STORED_HEALTH_STATUSES = ['absent', 'ok', 'warn', 'crit'] as const;
export type ReplicationStoredHealthStatus = (typeof REPLICATION_STORED_HEALTH_STATUSES)[number];

/** Publication / subscription / unqualified-slot name (tokyo→cn). Slot names are resolved in `replication-slot-name.mjs`. */
export const REPLICATION_DOWNLINK_SLOT = 'ndt_downlink';
/** Publication / subscription name (cn→tokyo). The uplink slot lives on CN. */
export const REPLICATION_UPLINK_SUBSCRIPTION = 'ndt_uplink';
/** CN-side subscription of the Tokyo→Shanghai publication. */
export const REPLICATION_DOWNLINK_SUBSCRIPTION = REPLICATION_DOWNLINK_SLOT;

export const REPLICATION_SAMPLE_RETENTION_DAYS = 90;
/** Two missed 5-minute samples. Older rows must not keep showing `ok`. */
export const REPLICATION_SAMPLE_STALE_AFTER_MS = 10 * 60 * 1000;

/** 512 MiB. */
export const REPLICATION_LAG_WARN_BYTES_DEFAULT = 512 * 1024 * 1024;
/** 2 GiB. */
export const REPLICATION_LAG_CRIT_BYTES_DEFAULT = 2 * 1024 * 1024 * 1024;
export const REPLICATION_RECEIPT_WARN_SECONDS_DEFAULT = 60;
export const REPLICATION_RECEIPT_CRIT_SECONDS_DEFAULT = 300;

export const REPLICATION_RUNBOOK_PATH = 'docs/runbooks/multiregion-replication.md';

export const REPLICATION_INIT_CONVERGE_COMMAND =
  './local-dev/ndt --dir <router-worktree> node router scripts/replication-init.mjs --converge';

export const REPLICATION_SELFHEAL_MODES = ['off', 'dry_run', 'active'] as const;
export type ReplicationSelfhealMode = (typeof REPLICATION_SELFHEAL_MODES)[number];

export const REPLICATION_RECOVERY_ACTIONS = [
  'terminate_apply_worker',
  'failback_reconnect',
  'breaker_open',
  'reenable_subscription',
] as const;
export type ReplicationRecoveryAction = (typeof REPLICATION_RECOVERY_ACTIONS)[number];

export const REPLICATION_RECOVERY_OUTCOMES = [
  'pending',
  'recovered',
  'not_recovered',
  'failed',
  'dry_run',
] as const;
export type ReplicationRecoveryOutcome = (typeof REPLICATION_RECOVERY_OUTCOMES)[number];

export const REPLICATION_UPLINK_PATHS = ['relay', 'direct', 'unknown'] as const;
export type ReplicationUplinkPath = (typeof REPLICATION_UPLINK_PATHS)[number];

export interface ReplicationRecoveryActionRecord {
  readonly id: string;
  readonly link_name: ReplicationLinkName;
  readonly action: ReplicationRecoveryAction;
  readonly acted_at: string;
  readonly reason: string;
  readonly target_pid: number | null;
  readonly outcome: ReplicationRecoveryOutcome;
  readonly outcome_at: string | null;
  readonly error: string | null;
}

/** Consecutive 5-minute samples required before a stall is acted on. */
export const REPLICATION_SELFHEAL_STALL_SAMPLES_DEFAULT = 2;
/** Receipt age (seconds) that counts as frozen when every sample exceeds it. */
export const REPLICATION_SELFHEAL_STALL_RECEIPT_SECONDS_DEFAULT = 600;
/** Terminate actions per link per hour before the breaker opens. */
export const REPLICATION_SELFHEAL_MAX_ACTIONS_PER_HOUR_DEFAULT = 3;
/** Minimum seconds between failback reconnects. */
export const REPLICATION_SELFHEAL_FAILBACK_MIN_INTERVAL_SECONDS_DEFAULT = 1800;
/** Samples to wait before a pending action becomes not_recovered. */
export const REPLICATION_SELFHEAL_OUTCOME_GRACE_SAMPLES = 2;
/** Failback breaker looks at this window. */
export const REPLICATION_SELFHEAL_FAILBACK_BREAKER_WINDOW_MS = 24 * 60 * 60 * 1000;
/** Consecutive not_recovered failbacks inside the window that open the breaker. */
export const REPLICATION_SELFHEAL_FAILBACK_BREAKER_FAILURES = 2;

export interface ReplicationHealthSample {
  readonly link_name: ReplicationLinkName;
  readonly status: ReplicationHealthStatus;
  readonly lag_bytes: number | null;
  readonly apply_lag_bytes: number | null;
  readonly receipt_age_seconds: number | null;
  readonly worker_count: number | null;
  readonly slot_active: boolean | null;
  readonly sampled_at: string;
  readonly publication_missing?: readonly string[];
  readonly publication_extra?: readonly string[];
  readonly worker_pid?: number | null;
  readonly received_lsn?: string | null;
  /** Only meaningful for link `uplink`. */
  readonly uplink_path?: ReplicationUplinkPath | null;
  /**
   * Uplink tables whose `pg_subscription_rel.srsubstate` is not `'r'`.
   * Null when the link is absent or this field was not sampled. Not a
   * `replication_health_samples` column — cron response / alert text only.
   */
  readonly stalled_relations?: number | null;
  /** Up to 10 relation names contributing to `stalled_relations`. */
  readonly stalled_relation_names?: readonly string[];
  /**
   * CN publisher slot for the uplink. Null when the CN route was not
   * observed. Not a `replication_health_samples` column.
   */
  readonly publisher_slot_present?: boolean | null;
  readonly publisher_slot_name?: string | null;
}

/**
 * How the sampler decides whether to page.
 *
 * - `muted` — `REPLICATION_MONITOR_EXPECTED=0`. Explicit hatch. Nothing pages.
 * - `armed` — default when EXPECTED is unset. Live-link `warn`/`crit`/recovery
 *   page; `absent` (uninitialized) stays quiet.
 * - `armed_including_absent` — `EXPECTED=1`. Also pages `absent`.
 */
export const REPLICATION_MONITOR_MODES = ['muted', 'armed', 'armed_including_absent'] as const;
export type ReplicationMonitorMode = (typeof REPLICATION_MONITOR_MODES)[number];

/** Sustained `crit` starts repeating every sample after this age. */
export const REPLICATION_CRIT_ESCALATE_MS = 15 * 60 * 1000;

export interface ReplicationSelfhealCronSummary {
  readonly mode: ReplicationSelfhealMode;
  readonly actions: readonly ReplicationRecoveryActionRecord[];
  readonly resolved: number;
  readonly breaker_open: { readonly uplink: boolean; readonly downlink: boolean };
  readonly failback: { readonly eligible: boolean; readonly skipped_reason: string | null };
  /** Set when the self-heal step threw; the tick still persisted samples. */
  readonly error?: string;
}

export interface ReplicationHealthCronResponse {
  readonly ok: boolean;
  /** False only when the monitor is explicitly muted (`EXPECTED=0`). */
  readonly expected: boolean;
  readonly monitor_mode: ReplicationMonitorMode;
  readonly webhook_configured: boolean;
  readonly samples: readonly ReplicationHealthSample[];
  readonly prunedCount: number;
  readonly notified: number;
  /** Samples that should have paged but did not (missing webhook or non-2xx). */
  readonly notify_failed: number;
  readonly selfheal: ReplicationSelfhealCronSummary;
}

export interface ListReplicationHealthResponse {
  readonly links: readonly ReplicationHealthSample[];
  readonly recent_actions: readonly ReplicationRecoveryActionRecord[];
}

export function isReplicationLinkName(value: unknown): value is ReplicationLinkName {
  return value === 'downlink' || value === 'uplink';
}

export function isReplicationHealthStatus(value: unknown): value is ReplicationHealthStatus {
  return value === 'absent'
    || value === 'ok'
    || value === 'warn'
    || value === 'crit'
    || value === 'unknown';
}

export function isReplicationStoredHealthStatus(
  value: unknown,
): value is ReplicationStoredHealthStatus {
  return value === 'absent' || value === 'ok' || value === 'warn' || value === 'crit';
}

/**
 * Read-time overlay. Stored `ok` is a lie when the cron is dead or
 * the downlink subscriber lag could not be observed.
 */
export function presentReplicationHealthStatus(params: {
  readonly stored: ReplicationStoredHealthStatus;
  readonly sampledAt: string;
  readonly nowMs: number;
  readonly linkName: ReplicationLinkName;
  readonly receiptAgeSeconds: number | null;
}): ReplicationHealthStatus {
  const sampledMs = Date.parse(params.sampledAt);
  if (!Number.isFinite(sampledMs) || params.nowMs - sampledMs > REPLICATION_SAMPLE_STALE_AFTER_MS) {
    return 'unknown';
  }
  if (
    params.linkName === 'downlink'
    && params.receiptAgeSeconds === null
    && params.stored === 'ok'
  ) {
    return 'unknown';
  }
  return params.stored;
}

export interface ReplicationPublisherPeer {
  readonly client_addr: string | null;
  readonly state: string | null;
  readonly backend_start: string | null;
}

/** CN gateway `GET /internal/replication-uplink-slot`. Region-agnostic: the asked gateway answers about its own database. */
export const REPLICATION_UPLINK_SLOT_MOUNT = '/internal/replication-uplink-slot';

export interface ReplicationUplinkSlotResponse {
  readonly slot_name: string;
  readonly present: boolean;
  readonly active: boolean | null;
  readonly unconfirmed_bytes: number | null;
}

/** CN gateway `GET /internal/replication-subscriber-lag`. */
export interface ReplicationSubscriberLagResponse {
  readonly subscription: string;
  readonly present: boolean;
  readonly worker_count: number | null;
  readonly receipt_age_seconds: number | null;
  readonly worker_pid?: number | null;
  readonly received_lsn?: string | null;
  readonly publisher_peers?: readonly ReplicationPublisherPeer[];
}

/** CN gateway `POST /internal/replication-subscriber-recover`. */
export interface ReplicationSubscriberRecoverRequest {
  readonly subscription: 'ndt_downlink';
  readonly pid: number;
}

export interface ReplicationSubscriberRecoverResponse {
  readonly terminated: boolean;
  readonly reason: 'terminated' | 'pid_not_found' | 'error';
  readonly error?: string;
}

export function isReplicationSelfhealMode(value: unknown): value is ReplicationSelfhealMode {
  return value === 'off' || value === 'dry_run' || value === 'active';
}

export function isReplicationRecoveryAction(value: unknown): value is ReplicationRecoveryAction {
  return value === 'terminate_apply_worker'
    || value === 'failback_reconnect'
    || value === 'breaker_open'
    || value === 'reenable_subscription';
}

export function isReplicationRecoveryOutcome(value: unknown): value is ReplicationRecoveryOutcome {
  return value === 'pending'
    || value === 'recovered'
    || value === 'not_recovered'
    || value === 'failed'
    || value === 'dry_run';
}

export function isReplicationUplinkPath(value: unknown): value is ReplicationUplinkPath {
  return value === 'relay' || value === 'direct' || value === 'unknown';
}

export function formatReplicationBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  const gib = 1024 * 1024 * 1024;
  const mib = 1024 * 1024;
  if (bytes >= gib) return `${(bytes / gib).toFixed(2)} GiB`;
  if (bytes >= mib) return `${(bytes / mib).toFixed(1)} MiB`;
  return `${bytes} B`;
}

export function formatReplicationAge(seconds: number | null): string {
  if (seconds === null) return '—';
  return `${Math.round(seconds)}s`;
}
